import React, { useState, useEffect } from "react";
import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import BookingDetails from "../Components/BookingDetails";
import {
  getDashboard,
  getPartnerDashboard,
} from "../Services/dashboardService";
import { logisticTypeColors, RoutesPaths } from "../Router/constant";
import { getBookings, getPartnerBookings } from "../Services/bookingService";
import { useNavigate } from "react-router-dom";
import { getAdminProfile, getPrivateFileBlob } from "../Services/userService";

export default function Dashboard() {
  Chart.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    LineController,
    BarController,
    DoughnutController,
    ArcElement,
  );
  const [dashboardData, setDashboardData] = useState({});
  const [bookings, setBookings] = useState([]);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    let timer;
    let tries = 0;

    const syncRole = () => {
      const role = localStorage.getItem("userRole");
      if (role) return setUserRole(role);

      if (tries++ < 15) timer = setTimeout(syncRole, 200);
    };

    syncRole();

    return () => clearTimeout(timer);
  }, []);

  const [userData, setUserData] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getAdminProfile();
        setUserData(res?.data?.data?.user);
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };
    fetchProfile();
  }, []);

  const handleViewClick = () => {
    const route = RoutesPaths.FREIGHTAGREEMENT;
    navigate(route);
  };

  const fetchBookings = async () => {
    setLoadingBookings(true);

    try {
      let response;

      if (userRole === "admin") {
        response = await getBookings();
      } else {
        response = await getPartnerBookings();
      }

      if (response?.data?.status === true) {
        setBookings(response.data.data.orders || []);
      } else {
        setBookings([]);
      }
    } catch (err) {
      setBookings([]);
      console.error(err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const fetchAllData = async () => {
    setLoadingDashboard(true);
    try {
      let dashboardResp;

      if (userRole === "admin") {
        dashboardResp = await getDashboard();
      } else {
        dashboardResp = await getPartnerDashboard();
      }

      setDashboardData(dashboardResp?.data?.data || {});
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoadingDashboard(false);
    }
  };

  useEffect(() => {
    if (!userRole) return;

    fetchAllData();
    fetchBookings();
  }, [userRole]);

  useEffect(() => {
    const canvas = document.getElementById("deliveryLineChart");
    if (!canvas || !dashboardData?.monthlyData) return;

    const ctx = canvas.getContext("2d");
    const ALL_MONTHS = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const apiLabels = dashboardData.monthlyData.labels || [];
    const apiRevenue = dashboardData.monthlyData.monthlyRevenue || [];

    const revenueMap = {};
    apiLabels.forEach((month, index) => {
      revenueMap[month] = apiRevenue[index] || 0;
    });

    const labels = ALL_MONTHS;
    const monthlyRevenue = ALL_MONTHS.map((month) => revenueMap[month] || 0);

    const existingChart = Chart.getChart("deliveryLineChart");
    if (existingChart) existingChart.destroy();

    new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Monthly Revenue",
            data: monthlyRevenue,
            borderColor: "rgba(231, 67, 22, 1)",
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 0,
            backgroundColor: (ctx) => {
              const gradient = ctx.chart.ctx.createLinearGradient(
                0,
                0,
                0,
                canvas.height,
              );
              gradient.addColorStop(0, "rgba(231, 67, 22, 0.4)");
              gradient.addColorStop(1, "rgba(23, 178, 106, 0)");
              return gradient;
            },
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: { display: false },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: (context) => `AOA ${context.raw.toLocaleString()}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
            border: { display: false },
          },
          y: {
            grid: { display: false, drawBorder: false },
            ticks: { display: false },
            border: { display: false },
          },
        },
      },
      plugins: [
        {
          id: "shadowLine",
          beforeDraw: (chart) => {
            const ctx = chart.ctx;
            ctx.save();
            ctx.shadowColor = "rgba(231, 67, 22, 0.5)";
            ctx.shadowBlur = 10;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 8;
          },
          afterDraw: (chart) => {
            chart.ctx.restore();
          },
        },
      ],
    });

    return () => {
      const chartInstance = Chart.getChart("deliveryLineChart");
      if (chartInstance) chartInstance.destroy();
    };
  }, [dashboardData]);

  useEffect(() => {
    const canvas = document.getElementById("deliveryDonutChartSlot");
    if (!canvas || !dashboardData?.logisticsBreakdown) return;

    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext("2d");

    const logisticsData = dashboardData.logisticsBreakdown;

    const formatLogisticsLabel = (type = "") =>
      type
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());

    const labels = logisticsData.map((item) =>
      formatLogisticsLabel(item.logisticsType),
    );
    const data = logisticsData.map((item) => item.count);

    const backgroundColor = logisticsData.map((item) => {
      const color = logisticTypeColors[item.logisticsType];
      return item.count > 0
        ? color || "rgba(211, 211, 211, 0.5)"
        : "rgba(211, 211, 211, 0.5)";
    });

    const existingChart = Chart.getChart("deliveryDonutChartSlot");
    if (existingChart) existingChart.destroy();

    new Chart(ctx, {
      type: "doughnut",
      data: {
        labels,
        datasets: [
          {
            label: "Logistics types",
            data,
            backgroundColor,
            borderWidth: 0,
          },
        ],
      },
      options: {
        responsive: true,
        cutout: "70%",
        plugins: {
          legend: { display: false },
          tooltip: { enabled: true },
        },
      },
      plugins: [
        {
          id: "centerText",
          beforeDraw: (chart) => {
            const { width, height, ctx } = chart;
            const total = data.reduce((sum, val) => sum + val, 0);
            const radius = Math.min(width, height) / 2;

            ctx.save();
            ctx.fillStyle = "#fff";
            ctx.beginPath();
            ctx.arc(width / 2, height / 2, radius * 0.7, 0, Math.PI * 2);
            ctx.fill();

            ctx.font = `bold 28px sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillStyle = "#000";
            ctx.fillText(`${total}`, width / 2, height / 2);
            ctx.restore();
          },
        },
      ],
    });

    return () => {
      const chartInstance = Chart.getChart("deliveryDonutChartSlot");
      if (chartInstance) chartInstance.destroy();
    };
  }, [dashboardData]);

  useEffect(() => {
    if (!dashboardData?.monthlyData) return;

    const ctx = document.getElementById("monthlyBarChart")?.getContext("2d");
    if (!ctx) return;

    const {
      labels,
      totalBookings,
      pendingBookings,
      inprogressBookings,
      transitBookings,
      deliveredBookings,
    } = dashboardData.monthlyData;

    const existingChart = Chart.getChart("monthlyBarChart");
    if (existingChart) existingChart.destroy();

    new Chart(ctx, {
      type: "bar",
      data: {
        labels,
        datasets: [
          {
            label: "Pending",
            data: pendingBookings,
            backgroundColor: "#FFC22E",
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.6,
          },
          {
            label: "In-progress",
            data: inprogressBookings,
            backgroundColor: "#387CF3",
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.6,
          },
          {
            label: "In-transit",
            data: transitBookings,
            backgroundColor: "#7062c9",
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.6,
          },

          {
            label: "Delivered",
            data: deliveredBookings,
            backgroundColor: "#60B526",
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.6,
          },
          {
            label: "Total",
            data: totalBookings,
            backgroundColor: "#F04927",
            borderRadius: 4,
            barPercentage: 0.5,
            categoryPercentage: 0.6,
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "#ddd",
            },
          },
          x: {
            ticks: {
              color: "#666",
            },
            grid: {
              display: false,
            },
          },
        },
        plugins: {
          legend: { display: true, position: "top" },
          tooltip: { enabled: true },
        },
      },
    });

    return () => {
      const chartInstance = Chart.getChart("monthlyBarChart");
      if (chartInstance) chartInstance.destroy();
    };
  }, [dashboardData]);
  const formatStatus = (status) => {
    const map = {
      pending: "Pending",
      completed: "Completed",
      inprogress: "In-progress",
      "in progress": "In-progress",
      "in-transit": "In-transit",
      "in transit": "In-transit",
    };

    return map[status?.toLowerCase()] || status;
  };

  const formatLogisticsType = (type = "") =>
    type
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/^./, (char) => char.toUpperCase());

  const completedBooking = dashboardData?.statusBreakdown?.find(
    (item) => item.status === "completed",
  );

  return (
    <div className="container-fluid">
      <div className="row dashboardSection">
        <div className="col-md-12">
          {userRole === "partner" &&
            userData?.partnershipContract?.status === "pending" && (
              <div className="contractManagementWrapper">
                <div className="contractCardNew">
                  <div>
                    <h5 className="cardTitle">
                      <img src="/assets/img/featuredIcon.png" alt="" />
                      <span style={{ marginLeft: "10px" }}>
                        Contract from Quimera team
                      </span>
                    </h5>

                    <p className="cardDesc">
                      Your air freight contract is now available. Read through
                      the details and sign to proceed with your vendor contract.
                    </p>

                    <button
                      className="btn viewBtn"
                      onClick={() => handleViewClick()}
                    >
                      View contract
                    </button>
                  </div>

                  <img
                    src="/assets/img/contract.png"
                    style={{
                      width: "250px",
                      height: "140px",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
            )}
          <br />
        </div>
        <div className="col-md-12 dashboardInnercomponent">
          <div className="leftDashboardSection">
            <div className="row">
              {/* Total Income */}
              {/* <div className="col-md-6 chartPadding" onClick={() => navigate(RoutesPaths.PAYMENT)}>
                <div className="incomeSecitonGraph">
                  <div className="incomeSecitonHeader">
                    <div className="incomeSecitonLeft">
                      <p className="m-0">Total incomes</p>
                      {loadingDashboard ? (
                        <div style={{ width: 80, height: 20, background: '#eee', borderRadius: 4 }} />
                      ) : (
                        <h2 className="m-0">AOA {Number(dashboardData?.totalRevenue || 0).toLocaleString()}
                        </h2>
                      )}
                    </div>
                  </div>
                  <div className="deliveryChart">
                    <canvas id="deliveryLineChart" className="p-0 m-0"></canvas>
                  </div>
                </div>
              </div> */}

              {/* Total bookings */}
              <div
                className="col-md-6 chartPadding"
                onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
              >
                <div className="totalBooking">
                  <p className="m-0 totalBookingtext">Total bookings</p>
                  {loadingDashboard
                    ? [...Array(3)].map((_, i) => (
                        <div className="outerLine tooltipWrapper" key={i}>
                          <div
                            style={{
                              width: "80%",
                              height: 8,
                              background: "#eee",
                              borderRadius: 4,
                            }}
                          />
                        </div>
                      ))
                    : dashboardData?.statusBreakdown?.map((item, index) => (
                        <div className="outerLine tooltipWrapper" key={index}>
                          <div
                            className={`activeLine ${item.status.toLowerCase().replace(/\s+/g, "")}Badge`}
                            style={{ width: item.percentage }}
                          ></div>
                          <span
                            className={`tooltipText ${item.status.toLowerCase().replace(/\s+/g, "")}Badge`}
                          >
                            {item.percentage}
                          </span>
                        </div>
                      ))}
                  <div className="totalBookingtrackOuter">
                    {loadingDashboard
                      ? [...Array(3)].map((_, i) => (
                          <div className="totalBookingtrack" key={i}>
                            <div className="totalBookingtrackHeader">
                              <div
                                className="badgeBooking"
                                style={{ backgroundColor: "#eee" }}
                              ></div>
                              <div
                                style={{
                                  width: 100,
                                  height: 10,
                                  background: "#eee",
                                  borderRadius: 4,
                                }}
                              />
                            </div>
                          </div>
                        ))
                      : dashboardData?.statusBreakdown?.map((item, index) => (
                          <div className="totalBookingtrack" key={index}>
                            <div className="totalBookingtrackHeader">
                              <div
                                className={`badgeBooking ${item.status
                                  .toLowerCase()
                                  .replace(/\s+/g, "")}Badge`}
                              ></div>

                              <p className="m-0 bookingStatusText d-flex align-items-center gap-1">
                                <span>{formatStatus(item.status)}</span>
                                <span className="bookingStatusCount">
                                  {item.count}
                                </span>
                              </p>
                            </div>
                          </div>
                        ))}
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div
                  className="donutChartSection mb-3  "
                  onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
                >
                  <div className="donutChartSectionData">
                    {loadingDashboard ? (
                      <div
                        style={{
                          width: 60,
                          height: 20,
                          background: "#eee",
                          borderRadius: 4,
                        }}
                      />
                    ) : (
                      <h6 className="m-0">
                        {dashboardData?.totalBookings || 0}
                      </h6>
                    )}
                    <p className="m-0">Total bookings</p>
                  </div>
                  <div className="donutChartSectionData">
                    <img
                      src="/assets/img/booking-dash.svg"
                      alt=""
                      className="customDashboardImg"
                    />
                  </div>
                </div>
                {userRole === "partner" && (
                  <div
                    className="donutChartSection mb-3  "
                    onClick={() => navigate(RoutesPaths.BOOKINGMANAGEMENT)}
                  >
                    <div className="donutChartSectionData">
                      {loadingDashboard ? (
                        <div
                          style={{
                            width: 60,
                            height: 20,
                            background: "#eee",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <h6 className="m-0">
                          <h6 className="m-0">
                            {completedBooking?.count || 0}
                          </h6>
                        </h6>
                      )}
                      <p className="m-0">Complete bookings</p>
                    </div>
                    <div className="donutChartSectionData">
                      <img
                        src="/assets/img/booking-dash.svg"
                        alt=""
                        className="customDashboardImg"
                      />
                    </div>
                  </div>
                )}

                {userRole === "admin" && (
                  <div
                    className="donutChartSection"
                    onClick={() => navigate(RoutesPaths.USERMANAGEMENT)}
                  >
                    <div className="donutChartSectionData">
                      {loadingDashboard ? (
                        <div
                          style={{
                            width: 60,
                            height: 20,
                            background: "#eee",
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        <h6 className="m-0">
                          {dashboardData?.totalUsers || 0}
                        </h6>
                      )}
                      <p className="m-0">Total users</p>
                    </div>
                    <div className="donutChartSectionData">
                      <img
                        src="/assets/img/user-dash.svg"
                        alt=""
                        className="customDashboardImg"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Reservation Stats */}
            <div className="row">
              <div className="col-md-12">
                <div className="reservationSlotShade">
                  <div className="reservationSlotHeader">
                    <div className="slotTrackingChart">
                      <h6 className="m-0">Booking stats</h6>
                    </div>
                  </div>
                  <div className="reservationBarStats">
                    <canvas
                      id="monthlyBarChart"
                      width="600"
                      height="300"
                    ></canvas>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Dashboard Section */}
          <div className="rightDashboardSection">
            <div className="deliveryDonutChartSlotSec">
              <h6 className="m-0 totalBookingtext">Total types</h6>
              <div className="deliveryDonutChartSlotOuter">
                <div className="deliveryDonutChartSlot">
                  <canvas
                    id="deliveryDonutChartSlot"
                    className="p-0 m-0"
                  ></canvas>
                </div>
              </div>

              {loadingDashboard
                ? [...Array(3)].map((_, i) => (
                    <div className="slotTrackingSection" key={i}>
                      <div className="totalBookingtrackHeader">
                        <div
                          className="legendColorBox"
                          style={{ backgroundColor: "#eee" }}
                        ></div>
                        <div
                          style={{
                            width: 80,
                            height: 10,
                            background: "#eee",
                            borderRadius: 4,
                          }}
                        />
                      </div>
                      <div
                        style={{
                          width: 50,
                          height: 10,
                          background: "#eee",
                          borderRadius: 4,
                        }}
                      />
                    </div>
                  ))
                : dashboardData?.logisticsBreakdown?.map((item, index) => {
                    const typeKey = item.logisticsType;
                    const color =
                      item.count > 0
                        ? logisticTypeColors[typeKey] || "#ccc"
                        : "#ccc";
                    return (
                      <div className="slotTrackingSection" key={index}>
                        <div className="totalBookingtrackHeader">
                          <div
                            className="legendColorBox"
                            style={{ backgroundColor: color }}
                          ></div>
                          <p className="m-0">{formatLogisticsType(typeKey)}</p>
                        </div>
                        <p className="m-0">
                          {item.count} {item.count > 1 ? "types" : "type"}
                        </p>
                      </div>
                    );
                  })}
            </div>

            {/* Customer reviews */}
            {userRole === "admin" && (
              <>
                {loadingDashboard ? (
                  <div style={{ padding: 20 }}>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        background: "#eee",
                        borderRadius: "50%",
                      }}
                    />
                    <div
                      style={{
                        marginTop: 10,
                        width: 120,
                        height: 20,
                        background: "#eee",
                      }}
                    />
                    <div
                      style={{
                        marginTop: 10,
                        width: 200,
                        height: 14,
                        background: "#eee",
                      }}
                    />
                    <div
                      style={{
                        marginTop: 10,
                        width: "100%",
                        height: 50,
                        background: "#eee",
                      }}
                    />
                  </div>
                ) : (
                  dashboardData?.reviews?.length > 0 && (
                    <div className="customerReviewOuter">
                      <div className="customerReviews">
                        <div className="slotTrackingChart">
                          <h6 className="m-0">Customer reviews</h6>
                          <p className="m-0">
                            {currentReviewIndex + 1} of{" "}
                            {dashboardData.reviews.length} Reviews
                          </p>
                        </div>
                        <div>
                          <img
                            src="/assets/img/arrow-left.svg"
                            alt="Previous"
                            onClick={() =>
                              setCurrentReviewIndex((prev) =>
                                prev > 0 ? prev - 1 : prev,
                              )
                            }
                            style={{
                              cursor:
                                currentReviewIndex > 0
                                  ? "pointer"
                                  : "not-allowed",
                              opacity: currentReviewIndex > 0 ? 1 : 0.4,
                              marginRight: 8,
                            }}
                          />
                          <img
                            src="/assets/img/arrow-right.svg"
                            alt="Next"
                            onClick={() =>
                              setCurrentReviewIndex((prev) =>
                                prev < dashboardData.reviews.length - 1
                                  ? prev + 1
                                  : prev,
                              )
                            }
                            style={{
                              cursor:
                                currentReviewIndex <
                                dashboardData.reviews.length - 1
                                  ? "pointer"
                                  : "not-allowed",
                              opacity:
                                currentReviewIndex <
                                dashboardData.reviews.length - 1
                                  ? 1
                                  : 0.4,
                            }}
                          />
                        </div>
                      </div>
                      {(() => {
                        const item = dashboardData.reviews[currentReviewIndex];
                        return (
                          <div key={currentReviewIndex}>
                            <div className="customerReviewProfile">
                              <img
                                src={
                                  item?.user?.profileImage
                                    ? `${process.env.REACT_APP_IMAGE_URL}/${item.user.profileImage}`
                                    : "/assets/img/default-profile.svg"
                                }
                                alt="User Profile"
                                width={60}
                                height={60}
                              />
                              <div>
                                <h6 className="m-0">
                                  {item?.user?.fullName || "Unknown"}
                                </h6>
                                <p className="m-0">
                                  {new Date(
                                    item?.createdAt,
                                  ).toLocaleDateString()}{" "}
                                  {new Date(
                                    item?.createdAt,
                                  ).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                            <div className="customerRatingStar">
                              {[...Array(5)].map((_, i) => (
                                <img
                                  key={i}
                                  src={
                                    i < item.rating
                                      ? "/assets/img/gold-star.svg"
                                      : "/assets/img/grey-star.svg"
                                  }
                                  alt="star"
                                />
                              ))}
                            </div>
                            <div>
                              <p className="m-0 customerDesc">{item.comment}</p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>

        {/* Bookings Section */}
        {loadingBookings ? (
          <div style={{ padding: 20 }}>
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                style={{
                  height: 60,
                  background: "#eee",
                  marginBottom: 10,
                  borderRadius: 6,
                }}
              />
            ))}
          </div>
        ) : (
          <BookingDetails data={bookings} fetchBookings={fetchBookings} />
        )}
      </div>
    </div>
  );
}
